import { createStyles, Paper } from '@material-ui/core';
import { debounce } from 'lodash-es';
import React from 'react';

import { signupUserWithToken } from '~/src/dash/api/console-api';
import { IObject } from '~/src/dash/api/resource-transforms';
import { AsyncButton } from '~/src/dash/components/button';
import {
  FormItemTypePassword,
  FormItemTypeText,
  IFormData,
  IFormItem,
  RenderForm,
} from '~/src/dash/components/form';
import { getColorTheme, getResellerTitle } from '~/src/dash/resellers/utils/importResellerConfig';
import { getContactEmail, getRoundLogoURL } from '~/src/dash/resellers/utils/importResellerConfig';
import { IHistoryProps, IReduxActionProps, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { ROLE_USER } from '~/src/dash/utils/roles';
import { hasErrors, IErrors, transformErrors, validatorFactory } from '~/src/dash/utils/validator';
import { BaseClass } from '~/src/dash/views/base-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps extends IWithStylesProps, IReduxActionProps, IHistoryProps {}

interface IState {
  data: IFormData;
  siteID: string;
  token: string;
  showLoadingWheel: boolean;
  count: number;

  errors: IErrors;
  snackbarMessage: string;
  snackbarError: boolean;
}

const invitationAcceptionForm: IFormItem[] = [
  {
    id: 'Email',
    label: 'Email',
    required: true,
    render: FormItemTypeText,
    props: {
      disabled: true,
    },
  },
  {
    id: 'FirstName',
    label: 'First Name',
    required: true,
    render: FormItemTypeText,
  },
  {
    id: 'LastName',
    label: 'Last Name',
    required: true,
    render: FormItemTypeText,
  },
  {
    id: 'Password',
    label: 'Password',
    required: true,
    render: FormItemTypePassword,
  },
  {
    id: 'ConfirmPassword',
    label: 'ConfirmPassword',
    required: true,
    render: FormItemTypePassword,
  },
];

const validate = validatorFactory({
  Password(val: any): string {
    if (!!val && val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    }
    return '';
  },
  ConfirmPassword(val: any, _key: string, _obj: IObject): string {
    if (!!val && val.length < 8) {
      return 'Your password needs to be at least 8 characters long.';
    } else if (val !== _obj.Password) {
      return 'Your passwords did not match';
    }
    return '';
  },
});

function extraParams(): string[] {
  const params = window.location.href.split('?')[1] || '';
  const parsedParams = {} as { [key: string]: string };

  params.split('&').forEach((p: string) => {
    const t = p.split('=');
    parsedParams[t[0]] = t[1];
  });

  return [parsedParams.email || '', parsedParams.site_id || '', parsedParams.token || ''];
}

export const InvitationAcceptionView = decorate(
  {
    styles: (theme: any) =>
      createStyles({
        paper: {
          ...theme.mixins.gutters(),
          paddingTop: theme.spacing.unit * 2,
          paddingBottom: theme.spacing.unit * 2,
          marginTop: theme.spacing.unit * 4,
          marginRight: 'auto',
          marginLeft: 'auto',
          maxWidth: '300px',
          [theme.breakpoints.up('md')]: {
            maxWidth: '400px',
            marginTop: theme.spacing.unit * 6,
          },
        },
        signHeader: {
          marginTop: theme.spacing.unit * 4,
          textAlign: 'center',
          [theme.breakpoints.up('md')]: {
            marginTop: 0, // Margin above root component
          },
        },
        signHeaderLogo: {
          marginTop: theme.spacing.unit * 8, // Margin above logo
          display: 'inline-block',
          width: '150px',
          height: '65px',
          background: 'url(' + getRoundLogoURL() + ') no-repeat center',
          backgroundSize: 'contain',
        },
        signHeaderText: {
          color: theme.palette.secondary.contrastText,
          fontSize: theme.spacing.unit * 4,
          margin: theme.spacing.unit * 3,
        },
      }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      data: {
        Email: extraParams()[0],
      } as IFormData,
      siteID: extraParams()[1],
      token: extraParams()[2],
      showLoadingWheel: false,
      count: 0,
      errors: {} as IErrors,
      snackbarMessage: '',
      snackbarError: false,
    };

    _signupUser = debounce((count: number) => {
      if (count !== this.state.count) {
        return;
      }
      signupUserWithToken({
        body: {
          ishb: true,
          token: this.state.token,
          site_id: this.state.siteID,
          email: this.state.data.Email,
          password: this.state.data.Password,
          first_name: this.state.data.FirstName,
          last_name: this.state.data.LastName,
          role: ROLE_USER,
        },
      }).then(
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState(
            {
              snackbarMessage: `Successfully created a ${getResellerTitle()} account via invitation. You will be redirected to sign in page in 3 seconds.`,
              snackbarError: false,
              showLoadingWheel: false,
            },
            () => {
              setTimeout(() => {
                window.location.href = '/#/signin';
              }, 3000);
            },
          );
        },
        () => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            snackbarMessage:
              'Creating account via invitation failed. Please try again later. If you still have problem, please contact ' +
              getContactEmail(),
            snackbarError: true,
            showLoadingWheel: false,
          });
        },
      );
    }, 200);

    constructor(props: IProps) {
      super(props);
    }

    signupUser = () => {
      if (!this.validateForm()) {
        this.setState({
          snackbarMessage:
            'Creating account via invitation failed. Please fix the errors and try again later.',
          snackbarError: true,
        });
        return;
      }
      const count = this.state.count + 1;
      this.setState({
        showLoadingWheel: true,
        count,
      });
      this._signupUser(count);
    };

    onUpdate = (data: IFormData) => {
      if (hasErrors(this.state.errors)) {
        this.validateForm();
      }
      this.setState({ data });
    };

    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.signupUser();
    };

    validateForm = (): boolean => {
      const errors = validate(this.state.data);
      if (hasErrors(errors)) {
        this.setState({
          errors,
        });
        return false;
      }
      this.setState({
        errors: {} as IErrors,
      });
      return true;
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    render = () => {
      const { classes } = this.props;
      return (
        <div
          style={{
            width: '100%',
            height: '100vh',
            backgroundColor: getColorTheme('signInBackground'),
            margin: '0px 0px 0px 0px',
          }}
        >
          <BaseClass id='signin'>
            <div className={classes.signHeader}>
              <div className={classes.signHeaderLogo} />
              <h3 className={classes.signHeaderText}>Sign Up</h3>
            </div>
            <Paper
              classes={{
                root: classes.paper,
              }}
            >
              <form id='signin-form-body' onSubmit={this.handleSubmit}>
                <RenderForm
                  schema={invitationAcceptionForm}
                  data={this.state.data}
                  errors={transformErrors(this.state.errors || {})}
                  onUpdate={this.onUpdate}
                />
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <AsyncButton
                    text='Sign Up'
                    inprogress={this.state.showLoadingWheel}
                    props={{
                      size: 'large',
                    }}
                    onClick={this.signupUser}
                  />
                </div>
              </form>
            </Paper>
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </BaseClass>
        </div>
      );
    };
  },
);
